/*
 * Copyright 2026 Oddpods
 *
 * This Source Code Form is subject to the terms of the Mozilla Public
 * License, v. 2.0. If a copy of the MPL was not distributed with this
 * file, You can obtain one at https://mozilla.org/MPL/2.0/.
 *
 * See https://github.com/oddpods/cauce/blob/main/LICENSE
 */
package cmp.navigation.rootnav

import androidx.lifecycle.viewModelScope
import kotlinx.coroutines.Job
import kotlinx.coroutines.flow.launchIn
import kotlinx.coroutines.flow.onEach
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch
import studio.oddpods.cauce.core.base.ui.BaseViewModel
import studio.oddpods.cauce.core.data.repository.cauce.ProfileRepository
import studio.oddpods.cauce.core.network.auth.AuthService
import studio.oddpods.cauce.core.network.auth.SessionStatus

class RootNavViewModel(
    private val authService: AuthService,
    private val profileRepository: ProfileRepository,
) : BaseViewModel<RootNavState, Unit, RootNavAction>(
    initialState = RootNavState.Splash,
) {

    private var profileLoadJob: Job? = null

    init {
        authService.sessionStatus
            .onEach { status -> handleAction(RootNavAction.SessionStatusChanged(status)) }
            .launchIn(viewModelScope)
    }

    override fun handleAction(action: RootNavAction) {
        when (action) {
            is RootNavAction.SessionStatusChanged -> onStatus(action.status)
            is RootNavAction.ProfileLoaded -> mutableStateFlow.update {
                if (action.onboardingCompleted) {
                    RootNavState.Authenticated
                } else {
                    RootNavState.NeedsOnboarding
                }
            }
            RootNavAction.ProfileLoadFailed -> mutableStateFlow.update { RootNavState.Splash }
        }
    }

    private fun onStatus(status: SessionStatus) {
        when (status) {
            SessionStatus.Initializing -> mutableStateFlow.update { RootNavState.Splash }
            SessionStatus.NotAuthenticated -> mutableStateFlow.update { RootNavState.NotAuthenticated }
            SessionStatus.Authenticated -> {
                mutableStateFlow.update { RootNavState.Splash }
                profileLoadJob?.cancel()
                profileLoadJob = viewModelScope.launch {
                    profileRepository.getCurrentProfile()
                        .onSuccess { trySendAction(RootNavAction.ProfileLoaded(it.onboardingCompleted)) }
                        .onFailure { trySendAction(RootNavAction.ProfileLoadFailed) }
                }
            }
        }
    }
}

sealed class RootNavState {
    data object Splash : RootNavState()
    data object NotAuthenticated : RootNavState()
    data object NeedsOnboarding : RootNavState()
    data object Authenticated : RootNavState()
}

sealed class RootNavAction {
    data class SessionStatusChanged(val status: SessionStatus) : RootNavAction()
    data class ProfileLoaded(val onboardingCompleted: Boolean) : RootNavAction()
    data object ProfileLoadFailed : RootNavAction()
}
